// frontend/src/components/OpenShiftsGridPanel.jsx
//
// MUI/dark-themed sibling of the "Open Shifts" grid on the staff HomeScreen
// (screens/HomeScreen.jsx's OpenShiftsGrid, built with react-bootstrap) — same
// bucketing logic and data sources, restyled to match the SLATE/NAVY/TEAL
// dashboard palette shared by SchedulingDashboard, ManagerDashboard and
// AgencyDashboard (admin + manager) so it drops into any of them.
//
// `defaultShowAllShifts` / `defaultAgencyViewOnly` only seed the initial
// switch state — both switches stay live so whoever's looking at the
// dashboard can still flip them.
import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { useSelector } from 'react-redux';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { useGetAvailableShiftsQuery, useGetWeeklyShiftsByWardQuery } from '../slices/shiftApiSlice';
import { useGetHospitalLocationsQuery } from '../slices/hospitalLocationApiSlice';

// ─── Palette — same hex values as SchedulingDashboard/ManagerDashboard/
// AgencyDashboard (not exported from those files, so duplicated here). ─────
const NAVY = '#0d1b2a';
const TEAL = '#0a8577';
const TEAL_L = '#0cb89e';
const SLATE = '#1e2d3d';
const MUTED = '#8fa3b1';
const WHITE = '#f0f4f8';

// Same preset windows as the Ward Weekly View / staff HomeScreen grid.
const SHIFT_TYPE_BUCKETS = [
  { key: 'LD', label: 'Long Day', start: '08:00', end: '20:00' },
  { key: 'ND', label: 'Night Duty', start: '20:00', end: '08:00' },
  { key: 'AM', label: 'Morning', start: '08:00', end: '16:00' },
  { key: 'PM', label: 'Afternoon', start: '14:00', end: '20:00' },
  { key: 'TW', label: 'Twilight', start: '20:00', end: '00:00' },
  { key: 'ELD', label: 'Extended Long Day', start: '07:30', end: '20:30' },
];
const ESCORT_ROW = { key: 'ESCORT', label: 'Escort' };
const GRID_ROWS = [...SHIFT_TYPE_BUCKETS, ESCORT_ROW];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const timeToMinutes = (t) => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const bucketDistance = (shiftStartMin, shiftEndMin, bucket) => {
  const bStart = timeToMinutes(bucket.start);
  let bEnd = timeToMinutes(bucket.end);
  if (bEnd <= bStart) bEnd += 1440;

  let sEnd = shiftEndMin;
  if (sEnd <= shiftStartMin) sEnd += 1440;

  return Math.abs(shiftStartMin - bStart) + Math.abs(sEnd - bEnd);
};

const pickBucketKey = (startTime, endTime) => {
  const sMin = timeToMinutes(startTime);
  const eMin = timeToMinutes(endTime);
  let best = SHIFT_TYPE_BUCKETS[0];
  let bestDist = Infinity;
  SHIFT_TYPE_BUCKETS.forEach((bucket) => {
    const dist = bucketDistance(sMin, eMin, bucket);
    if (dist < bestDist) {
      bestDist = dist;
      best = bucket;
    }
  });
  return best.key;
};

const toLocalISO = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getWeekStart = (date) => {
  const current = new Date(date);
  current.setHours(0, 0, 0, 0);
  const day = current.getDay();
  const diff = current.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(current);
  monday.setDate(diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

// Same "agency shift" definition getAgencyShifts() uses on the backend:
// filledByAgency or actualFilledByAgency set. allowAgencyView alone only
// ever applies to a still-Open, unfilled shift.
const isAgencyShift = (shift) =>
  shift.allowAgencyView === true || !!shift.filledByAgency || !!shift.actualFilledByAgency;

// Manager ward-scoping — identical logic to ManagerDashboard.jsx /
// manager/Dashboard/AgencyDashboard.jsx's `directorateWards`, so a manager
// sees the same wards here as everywhere else on their dashboard. Admins
// (and anyone with no manager role at all, e.g. a reporter) get every ward,
// unscoped — this only narrows things down for isManager && !isAdmin.
const normalizeDirectorate = (val) => (val || '').trim().toLowerCase();

const useDirectorateWards = () => {
  const { userInfo } = useSelector((s) => s.auth);
  const isAdmin = userInfo?.isAdmin === true;
  const isManager = userInfo?.isManager === true;
  const managerDirectorate = userInfo?.directorateName || null;
  const managerOnlyOwnWard = userInfo?.onlyOwnWard === true;

  const { data: hospitalLocations = [] } = useGetHospitalLocationsQuery(undefined, {
    skip: isAdmin || !isManager,
  });

  const inclusionIds = useMemo(() => {
    const managerWardInclusions = userInfo?.wardAreaInclusions || [];
    return new Set(managerWardInclusions.map((w) => (typeof w === 'object' && w !== null ? w._id : w)));
  }, [userInfo]);

  const normalizedManagerDirectorate = useMemo(
    () => normalizeDirectorate(managerDirectorate),
    [managerDirectorate]
  );

  // null = unscoped (admin, or any non-manager role) — every shift passes.
  const directorateWards = useMemo(() => {
    if (isAdmin || !isManager) return null;

    if (managerOnlyOwnWard) {
      return new Set(
        hospitalLocations
          .filter((l) => l.wardDepartmentArea === userInfo?.speciality || inclusionIds.has(l._id))
          .map((l) => l.wardDepartmentArea)
          .filter(Boolean)
      );
    }

    if (!managerDirectorate && inclusionIds.size === 0) return new Set();

    return new Set(
      hospitalLocations
        .filter(
          (l) =>
            normalizeDirectorate(l.directorate) === normalizedManagerDirectorate || inclusionIds.has(l._id)
        )
        .map((l) => l.wardDepartmentArea)
        .filter(Boolean)
    );
  }, [hospitalLocations, isAdmin, isManager, managerOnlyOwnWard, managerDirectorate, normalizedManagerDirectorate, inclusionIds, userInfo?.speciality]);

  return { directorateWards, isManagerScoped: isManager && !isAdmin, managerDirectorate, managerOnlyOwnWard };
};

const OpenShiftsGridPanel = ({ defaultShowAllShifts = false, defaultAgencyViewOnly = false }) => {
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [agencyViewOnly, setAgencyViewOnly] = useState(defaultAgencyViewOnly);
  const [showAllShifts, setShowAllShifts] = useState(defaultShowAllShifts);

  const { directorateWards, isManagerScoped, managerDirectorate, managerOnlyOwnWard } = useDirectorateWards();

  const weekDays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);
        return { date: toLocalISO(d), label: DAY_LABELS[i] };
      }),
    [weekStart]
  );

  const startDate = toLocalISO(weekStart);
  const endDate = weekDays[6]?.date || startDate;

  const { data: openShifts, isLoading: isLoadingOpen, refetch: refetchOpen } = useGetAvailableShiftsQuery(
    { startDate, endDate },
    { skip: showAllShifts }
  );

  // No `ward` passed — every ward, every non-cancelled shift in range, same
  // source the Ward Weekly View itself uses.
  const { data: allShifts, isLoading: isLoadingAll, refetch: refetchAll } = useGetWeeklyShiftsByWardQuery(
    { startDate, endDate },
    { skip: !showAllShifts }
  );

  const isLoading = showAllShifts ? isLoadingAll : isLoadingOpen;
  const shiftsForGrid = showAllShifts ? allShifts : openShifts;
  const refetch = () => (showAllShifts ? refetchAll() : refetchOpen());

  const grid = useMemo(() => {
    const counts = {};
    GRID_ROWS.forEach((row) => {
      counts[row.key] = {};
    });

    (shiftsForGrid || [])
      .filter((shift) => !directorateWards || directorateWards.has(shift.wardDepartmentArea))
      .filter((shift) => !agencyViewOnly || isAgencyShift(shift))
      .forEach((shift) => {
        const start = new Date(shift.startDateTime);
        const end = new Date(shift.endDateTime);
        const dayKey = toLocalISO(start);
        const startTime = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
        const endTime = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;

        const bucketKey = shift.escort ? ESCORT_ROW.key : pickBucketKey(startTime, endTime);
        counts[bucketKey][dayKey] = (counts[bucketKey][dayKey] || 0) + 1;
      });

    return counts;
  }, [shiftsForGrid, agencyViewOnly, directorateWards]);

  const rowTotals = useMemo(() => {
    const totals = {};
    GRID_ROWS.forEach((row) => {
      totals[row.key] = weekDays.reduce((sum, day) => sum + (grid[row.key]?.[day.date] || 0), 0);
    });
    return totals;
  }, [grid, weekDays]);

  const dayTotals = useMemo(() => {
    const totals = {};
    weekDays.forEach((day) => {
      totals[day.date] = GRID_ROWS.reduce((sum, row) => sum + (grid[row.key]?.[day.date] || 0), 0);
    });
    return totals;
  }, [grid, weekDays]);

  const weekTotal = useMemo(() => Object.values(rowTotals).reduce((sum, n) => sum + n, 0), [rowTotals]);

  const shiftWeek = (deltaWeeks) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + deltaWeeks * 7);
    setWeekStart(getWeekStart(d));
  };

  const isThisWeek = startDate === toLocalISO(getWeekStart(new Date()));

  const switchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: TEAL_L },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: TEAL },
  };
  const labelSx = { color: MUTED, fontSize: '0.72rem', fontFamily: '"DM Mono",monospace' };

  return (
    <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CalendarViewWeekIcon sx={{ color: TEAL, fontSize: 20 }} />
          <Typography sx={{ fontFamily: '"DM Mono",monospace', fontWeight: 700, fontSize: '0.85rem',
            letterSpacing: 1, textTransform: 'uppercase', color: WHITE }}>
            {showAllShifts ? 'All Shifts' : 'Open Shifts'}
          </Typography>
          {isManagerScoped && (
            <Typography
              title="This grid only counts wards in your directorate (plus any admin-assigned inclusions) — same scope as the rest of this dashboard"
              sx={{ color: MUTED, fontSize: '0.68rem', fontFamily: '"DM Mono",monospace' }}
            >
              · {managerOnlyOwnWard ? 'my ward' : managerDirectorate || 'no directorate assigned'}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <FormControlLabel
            sx={{ ml: 0 }}
            control={
              <Switch
                size="small"
                sx={switchSx}
                checked={showAllShifts}
                onChange={(e) => setShowAllShifts(e.target.checked)}
              />
            }
            label={<Typography sx={labelSx}>Show all shifts</Typography>}
            title="Show every shift this week (filled or not), instead of just Open ones"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                sx={switchSx}
                checked={agencyViewOnly}
                onChange={(e) => setAgencyViewOnly(e.target.checked)}
              />
            }
            label={
              <Typography sx={labelSx}>
                {showAllShifts ? 'Agency shifts only' : 'Agency-visible only'}
              </Typography>
            }
            title={
              showAllShifts
                ? 'Show only shifts an agency is filling or has filled, or that are flagged visible to agencies'
                : 'Show only Open shifts flagged as visible to agencies'
            }
          />

          <IconButton size="small" onClick={() => shiftWeek(-1)} sx={{ color: MUTED, '&:hover': { color: TEAL_L } }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ color: MUTED, fontSize: '0.75rem', fontFamily: '"DM Mono",monospace', whiteSpace: 'nowrap' }}>
            {startDate} – {endDate}{isThisWeek ? ' (This Week)' : ''}
          </Typography>
          <IconButton size="small" onClick={() => shiftWeek(1)} sx={{ color: MUTED, '&:hover': { color: TEAL_L } }}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          {!isThisWeek && (
            <Typography
              onClick={() => setWeekStart(getWeekStart(new Date()))}
              sx={{ color: TEAL_L, fontSize: '0.72rem', fontFamily: '"DM Mono",monospace', cursor: 'pointer',
                textDecoration: 'underline', whiteSpace: 'nowrap' }}
            >
              This Week
            </Typography>
          )}
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={refetch} disabled={isLoading} sx={{ color: MUTED, '&:hover': { color: TEAL_L } }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} sx={{ color: TEAL }} />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: MUTED, fontFamily: '"DM Mono",monospace', fontSize: '0.68rem',
                  letterSpacing: 1, textTransform: 'uppercase', borderColor: `${TEAL}22`, width: '18%' }}>
                  Shift Type
                </TableCell>
                {weekDays.map((day) => (
                  <TableCell key={day.date} align="center" sx={{ color: MUTED, fontFamily: '"DM Mono",monospace',
                    fontSize: '0.68rem', letterSpacing: 1, textTransform: 'uppercase', borderColor: `${TEAL}22` }}>
                    {day.label}
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ color: MUTED, fontFamily: '"DM Mono",monospace', fontSize: '0.68rem',
                  letterSpacing: 1, textTransform: 'uppercase', borderColor: `${TEAL}22` }}>
                  Total
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {GRID_ROWS.map((row) => (
                <TableRow key={row.key}>
                  <TableCell sx={{ color: WHITE, fontFamily: '"DM Mono",monospace', fontSize: '0.78rem',
                    fontWeight: 700, borderColor: `${TEAL}14` }}>
                    {row.label}
                  </TableCell>
                  {weekDays.map((day) => {
                    const count = grid[row.key]?.[day.date] || 0;
                    return (
                      <TableCell key={day.date} align="center" sx={{
                        color: count > 0 ? TEAL_L : MUTED, fontFamily: '"DM Mono",monospace', fontSize: '0.78rem',
                        fontWeight: count > 0 ? 700 : 400, borderColor: `${TEAL}14`,
                        bgcolor: count > 0 ? `${TEAL}18` : 'transparent',
                      }}>
                        {count}
                      </TableCell>
                    );
                  })}
                  <TableCell align="center" sx={{ color: WHITE, fontFamily: '"DM Mono",monospace', fontSize: '0.78rem',
                    fontWeight: 700, borderColor: `${TEAL}14` }}>
                    {rowTotals[row.key] || 0}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ color: WHITE, fontFamily: '"DM Mono",monospace', fontSize: '0.78rem',
                  fontWeight: 700, borderColor: 'transparent', bgcolor: `${NAVY}` }}>
                  Total
                </TableCell>
                {weekDays.map((day) => (
                  <TableCell key={day.date} align="center" sx={{ color: WHITE, fontFamily: '"DM Mono",monospace',
                    fontSize: '0.78rem', fontWeight: 700, borderColor: 'transparent', bgcolor: NAVY }}>
                    {dayTotals[day.date] || 0}
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ color: TEAL_L, fontFamily: '"DM Mono",monospace', fontSize: '0.85rem',
                  fontWeight: 700, borderColor: 'transparent', bgcolor: NAVY }}>
                  {weekTotal}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
};

export default OpenShiftsGridPanel;
